import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import axios from 'axios';
import { format } from 'date-fns';
import InlineFeedback from '../components/InlineFeedback';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) {
    return envBase.trim().replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const currentHost = window.location.hostname;
    if (currentHost === 'localhost' || currentHost === '::1' || currentHost === '[::1]') {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${currentHost}:8000/api`;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`;
const HOLD_TO_DELETE_MS = 1200;
const WEEKDAY_OPTIONS = [
  { key: 0, short: 'Sun', label: 'Sunday' },
  { key: 1, short: 'Mon', label: 'Monday' },
  { key: 2, short: 'Tue', label: 'Tuesday' },
  { key: 3, short: 'Wed', label: 'Wednesday' },
  { key: 4, short: 'Thu', label: 'Thursday' },
  { key: 5, short: 'Fri', label: 'Friday' },
  { key: 6, short: 'Sat', label: 'Saturday' },
];

const WorkerAvailability = () => {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [userProfile, setUserProfile] = useState(null);

  const [message, setMessage] = useState('');
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isAddSubmitting, setIsAddSubmitting] = useState(false);
  const [isDeleteSubmitting, setIsDeleteSubmitting] = useState(false);
  const [isRecurringEnabled, setIsRecurringEnabled] = useState(false);
  const [recurringStartDate, setRecurringStartDate] = useState('');
  const [recurringEndDate, setRecurringEndDate] = useState('');
  const [recurringWeekdays, setRecurringWeekdays] = useState([1, 2, 3, 4, 5]);

  const [selectedForDeletion, setSelectedForDeletion] = useState([]);
  const [isDeleteHoldActive, setIsDeleteHoldActive] = useState(false);
  const [deleteHoldProgress, setDeleteHoldProgress] = useState(0);
  const deleteHoldIntervalRef = useRef(null);

  const timeSlots = Array.from({ length: 40 }, (_, index) => {
    const minutes = (8 * 60) + (index * 15);
    return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  });

  const buildWorkingWindows = (times) => {
    const minutes = [...new Set(times)].map((time) => {
      const [hour, minute] = String(time).split(':').map(Number);
      return (hour * 60) + minute;
    }).sort((a, b) => a - b);
    if (!minutes.length) return [];
    const windows = [];
    let start = minutes[0];
    let previous = minutes[0];
    const format = (value) => `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;
    minutes.slice(1).forEach((current) => {
      if (current - previous > 15) {
        windows.push({ start: format(start), end: format(previous + 15) });
        start = current;
      }
      previous = current;
    });
    windows.push({ start: format(start), end: format(previous + 15) });
    return windows;
  };

  const buildAuthHeaders = useCallback((accessToken = token) => ({
    headers: { Authorization: `Bearer ${accessToken}` },
  }), [token]);

  const clearSessionAndRedirect = useCallback((reasonMessage = 'Session expired. Please log in again.') => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userRole');
    window.dispatchEvent(new Event('auth-changed'));
    setMessage(`❌ ${reasonMessage}`);
    navigate('/login');
  }, [navigate]);

  const stopDeleteHold = useCallback(() => {
    if (deleteHoldIntervalRef.current) {
      clearInterval(deleteHoldIntervalRef.current);
      deleteHoldIntervalRef.current = null;
    }
    setIsDeleteHoldActive(false);
    setDeleteHoldProgress(0);
  }, []);

  const fetchSchedule = useCallback(async (accessToken = token) => {
    try {
      const response = await axios.get(apiUrl('schedules/'), buildAuthHeaders(accessToken));
      const rows = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.results)
          ? response.data.results
          : [];
      setSchedule(rows);
    } catch (error) {
      console.error('Failed to load schedule:', error);
      if (error?.response?.status === 401 || error?.response?.data?.code === 'token_not_valid') {
        clearSessionAndRedirect();
        return;
      }
      setMessage('❌ Unable to load schedule. Please verify backend is running on port 8000.');
    }
  }, [token, buildAuthHeaders, clearSessionAndRedirect]);

  useEffect(() => {
    if (!token) {
      setMessage('❌ Please log in to manage availability.');
      setIsPageLoading(false);
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        setIsPageLoading(true);
        const response = await axios.get(apiUrl('userprofiles/me/'), buildAuthHeaders());
        setUserProfile(response.data);

        if (!['worker', 'owner'].includes(response.data.role)) {
          navigate('/');
          return;
        }

        await fetchSchedule();
      } catch (error) {
        console.error('Error fetching user profile:', error);
        if (error?.response?.status === 401 || error?.response?.data?.code === 'token_not_valid') {
          clearSessionAndRedirect();
          return;
        }
        setMessage('❌ Failed to load profile data.');
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchData();
  }, [token, navigate, buildAuthHeaders, clearSessionAndRedirect, fetchSchedule]);

  useEffect(() => () => stopDeleteHold(), [stopDeleteHold]);

  const handleTimeToggle = (time) => {
    setSelectedTimes((prev) => (
      prev.includes(time)
        ? prev.filter((value) => value !== time)
        : [...prev, time]
    ));
    setMessage('');
  };

  const parseLocalDate = (dateString) => {
    const [yearRaw, monthRaw, dayRaw] = String(dateString || '').split('-');
    const year = Number(yearRaw);
    const month = Number(monthRaw);
    const day = Number(dayRaw);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  };

  const recurringGeneratedDates = useMemo(() => {
    if (!isRecurringEnabled) return [];
    if (!recurringStartDate || !recurringEndDate || recurringWeekdays.length === 0) return [];

    const start = parseLocalDate(recurringStartDate);
    const end = parseLocalDate(recurringEndDate);
    if (!start || !end || end < start) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const generated = [];
    const cursor = new Date(start);

    while (cursor <= end) {
      if (cursor >= today && recurringWeekdays.includes(cursor.getDay())) {
        generated.push(format(cursor, 'yyyy-MM-dd'));
      }
      cursor.setDate(cursor.getDate() + 1);
    }

    return generated;
  }, [isRecurringEnabled, recurringStartDate, recurringEndDate, recurringWeekdays]);

  const toggleRecurringWeekday = (weekday) => {
    setRecurringWeekdays((prev) => (
      prev.includes(weekday)
        ? prev.filter((value) => value !== weekday)
        : [...prev, weekday].sort((a, b) => a - b)
    ));
    setMessage('');
  };

  const handleAddAvailability = async () => {
    const datesToApply = Array.from(new Set([...selectedDates, ...recurringGeneratedDates])).sort();

    if (datesToApply.length === 0 || selectedTimes.length === 0) {
      setMessage('❌ Please select at least one date (manual or recurring) and one time slot.');
      return;
    }

    if (!userProfile?.id) {
      setMessage('❌ Worker profile is still loading. Please try again.');
      return;
    }

    try {
      setMessage('');
      setIsAddSubmitting(true);
      let updatedSchedules = [...schedule];
      const formattedTimes = selectedTimes.map((time) => time);

      for (const date of datesToApply) {
        const existingSchedule = updatedSchedules.find((entry) => entry.date === date);

        if (existingSchedule) {
          const updatedTimeSlots = [...new Set([...(existingSchedule.time_slots || []), ...formattedTimes])];

          await axios.put(
            apiUrl(`schedules/${existingSchedule.id}/`),
            {
              date,
              time_slots: updatedTimeSlots,
              working_windows: buildWorkingWindows(updatedTimeSlots),
              worker: userProfile.id,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );

          updatedSchedules = updatedSchedules.map((entry) => (
            entry.id === existingSchedule.id
              ? { ...entry, time_slots: updatedTimeSlots, working_windows: buildWorkingWindows(updatedTimeSlots) }
              : entry
          ));
        } else {
          const response = await axios.post(
            apiUrl('schedules/'),
            {
              date,
              time_slots: formattedTimes,
              working_windows: buildWorkingWindows(formattedTimes),
              worker: userProfile.id,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );

          updatedSchedules.push(response.data);
        }
      }

      setSchedule(updatedSchedules);
      setSelectedDates([]);
      setSelectedTimes([]);
      if (isRecurringEnabled) {
        setRecurringStartDate('');
        setRecurringEndDate('');
      }
      setMessage('✅ Availability added!');
      await fetchSchedule();
    } catch (error) {
      console.error('Error saving availability:', error?.response?.data || error.message);
      if (error?.response?.status === 401 || error?.response?.data?.code === 'token_not_valid') {
        clearSessionAndRedirect();
        return;
      }
      const apiMessage = error?.response?.data?.error;
      setMessage(apiMessage ? `❌ ${apiMessage}` : '❌ Failed to add availability.');
    } finally {
      setIsAddSubmitting(false);
    }
  };

  const toggleSelectTime = (date, time) => {
    const id = `${date}|${time}`;
    setSelectedForDeletion((prev) => (
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    ));
    setMessage('');
  };

  const handleDeleteSelected = async () => {
    if (!selectedForDeletion.length) return;

    try {
      setIsDeleteSubmitting(true);
      const updatedSchedules = [...schedule];

      const groupedByDate = selectedForDeletion.reduce((acc, id) => {
        const [date, time] = id.split('|');
        if (!acc[date]) acc[date] = [];
        acc[date].push(time);
        return acc;
      }, {});

      const promises = Object.entries(groupedByDate).map(async ([date, timesToDelete]) => {
        const scheduleEntry = updatedSchedules.find((entry) => entry.date === date);
        if (!scheduleEntry) return;

        const remainingTimes = (scheduleEntry.time_slots || []).filter((time) => !timesToDelete.includes(time));

        if (remainingTimes.length > 0) {
          await axios.put(
            apiUrl(`schedules/${scheduleEntry.id}/`),
            {
              date,
              time_slots: remainingTimes,
              working_windows: buildWorkingWindows(remainingTimes),
              worker: userProfile?.id,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );
        } else {
          await axios.delete(apiUrl(`schedules/${scheduleEntry.id}/`), buildAuthHeaders());
        }
      });

      await Promise.all(promises);
      setSelectedForDeletion([]);
      setMessage('✅ Selected availability deleted.');
      await fetchSchedule();
    } catch (error) {
      console.error('Failed to delete selected times:', error);
      if (error?.response?.status === 401 || error?.response?.data?.code === 'token_not_valid') {
        clearSessionAndRedirect();
        return;
      }
      setMessage('❌ Error deleting selected availability.');
    } finally {
      setIsDeleteSubmitting(false);
      stopDeleteHold();
    }
  };

  const startDeleteHold = () => {
    if (!selectedForDeletion.length || isDeleteSubmitting) return;

    stopDeleteHold();
    setIsDeleteHoldActive(true);
    const startedAt = Date.now();

    deleteHoldIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const nextProgress = Math.min(100, (elapsed / HOLD_TO_DELETE_MS) * 100);
      setDeleteHoldProgress(nextProgress);

      if (elapsed >= HOLD_TO_DELETE_MS) {
        stopDeleteHold();
        handleDeleteSelected();
      }
    }, 40);
  };

  const handleDateClick = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    setSelectedDates((prev) => (
      prev.includes(dateStr)
        ? prev.filter((value) => value !== dateStr)
        : [...prev, dateStr]
    ));
    setMessage('');
  };

  const disablePastDates = ({ date }) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const getFutureTimeSlots = useCallback((entry) => {
    const now = new Date();
    const todayKey = format(now, 'yyyy-MM-dd');
    const entryDate = entry?.date;

    if (!entryDate || entryDate < todayKey) return [];

    return (entry?.time_slots || [])
      .filter((slot) => {
        if (entryDate > todayKey) return true;

        const [hourRaw, minuteRaw] = String(slot).split(':');
        const hour = Number(hourRaw);
        const minute = Number(minuteRaw);
        if (Number.isNaN(hour) || Number.isNaN(minute)) return false;

        return hour * 60 + minute > now.getHours() * 60 + now.getMinutes();
      })
      .sort();
  }, []);

  const upcomingSchedule = useMemo(
    () => schedule
      .map((entry) => ({ ...entry, future_times: getFutureTimeSlots(entry) }))
      .filter((entry) => entry.future_times.length > 0)
      .sort((a, b) => String(a.date).localeCompare(String(b.date))),
    [schedule, getFutureTimeSlots]
  );

  const bookedDateSet = useMemo(
    () => new Set(upcomingSchedule.map((entry) => entry.date)),
    [upcomingSchedule]
  );

  const upcomingSlotCount = useMemo(
    () => upcomingSchedule.reduce((count, entry) => count + entry.future_times.length, 0),
    [upcomingSchedule]
  );

  const totalDatesToApply = useMemo(
    () => Array.from(new Set([...selectedDates, ...recurringGeneratedDates])).length,
    [selectedDates, recurringGeneratedDates]
  );

  const feedbackType = message.startsWith('❌')
    ? 'error'
    : message.startsWith('⚠️')
      ? 'warning'
      : 'success';

  const isOwner = userProfile?.role === 'owner';

  return (
    <div className="min-h-screen bg-[#F6F4F8] px-3 py-4 text-[#2E2E2E] sm:px-4 sm:py-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="rounded-2xl bg-gradient-to-r from-[#6D3B7E] to-[#8A4AA3] p-4 text-white shadow-lg sm:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold sm:text-3xl">{isOwner ? 'Owner Availability' : 'Worker Availability'}</h1>
              <p className="mt-2 text-sm text-purple-100 md:text-base">
                Set open time slots, keep your calendar current, and prevent scheduling gaps.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="rounded-lg bg-white/15 px-4 py-2 text-sm font-semibold text-white backdrop-blur hover:bg-white/25"
              >
                🏠 Home
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="rounded-lg bg-[#CDA349] px-4 py-2 text-sm font-semibold text-white hover:bg-[#b8913e]"
              >
                ↩ Back
              </button>
            </div>
          </div>
        </header>

        {message ? <InlineFeedback type={feedbackType} message={message} /> : null}
        {isPageLoading ? <InlineFeedback type="info" message="Loading your availability..." /> : null}
        {isOwner && !isPageLoading ? (
          <InlineFeedback
            type="info"
            message="Publishing upcoming availability lists you as a bookable professional for clients."
          />
        ) : null}

        <section className="rounded-2xl bg-white p-4 shadow-sm" aria-label="Availability summary stats">
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full bg-[#F4EBF8] px-3 py-1.5 text-xs font-semibold text-[#6D3B7E]">
              Selected dates: {selectedDates.length}
            </span>
            <span className="rounded-full bg-[#EEF2FF] px-3 py-1.5 text-xs font-semibold text-[#3949AB]">
              Recurring dates: {recurringGeneratedDates.length}
            </span>
            <span className="rounded-full bg-[#E8F7EF] px-3 py-1.5 text-xs font-semibold text-[#0F766E]">
              Selected slots: {selectedTimes.length}
            </span>
            <span className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700">
              Upcoming dates: {upcomingSchedule.length}
            </span>
            <span className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700">
              Upcoming slots: {upcomingSlotCount}
            </span>
          </div>
        </section>

        <section className="rounded-2xl bg-white p-5 shadow-sm" aria-label="Calendar date selection">
          <h2 className="text-lg font-semibold text-[#4B2A56]">Calendar: Select Dates</h2>
          <p className="mt-1 text-sm text-gray-600">
            Pick one or multiple dates, then choose the time slots you want to open.
          </p>
          <div className="mt-4 overflow-x-auto">
            <Calendar
              onClickDay={handleDateClick}
              tileDisabled={disablePastDates}
              tileClassName={({ date }) => {
                const dateStr = format(date, 'yyyy-MM-dd');
                if (selectedDates.includes(dateStr)) return 'selected-date';
                if (bookedDateSet.has(dateStr)) return 'highlight';
                return null;
              }}
              className="w-full max-w-3xl rounded-xl border border-gray-200 p-2"
            />
          </div>
        </section>

        <section className="rounded-2xl bg-white p-5 shadow-sm" aria-label="Time slot selection and add flow">
          <h2 className="text-lg font-semibold text-[#4B2A56]">Time Slots: Add Availability</h2>
          <p className="mt-1 text-sm text-gray-600">
            Selected dates: {selectedDates.length === 0 ? 'none' : selectedDates.join(', ')}
          </p>
          <p className="mt-1 text-sm text-gray-600">
            Total dates to apply: <strong>{totalDatesToApply}</strong>
          </p>

          <div className="mt-4 rounded-xl border border-[#DCE6FF] bg-[#F7F9FF] p-4">
            <label className="flex items-center gap-2 text-sm font-semibold text-[#304A8A]">
              <input
                type="checkbox"
                checked={isRecurringEnabled}
                onChange={(event) => setIsRecurringEnabled(event.target.checked)}
                className="h-4 w-4"
              />
              Add recurring schedule for a date range
            </label>

            {isRecurringEnabled ? (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="text-sm text-gray-700">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Start Date</span>
                    <input
                      type="date"
                      value={recurringStartDate}
                      onChange={(event) => setRecurringStartDate(event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="text-sm text-gray-700">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">End Date</span>
                    <input
                      type="date"
                      value={recurringEndDate}
                      onChange={(event) => setRecurringEndDate(event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </label>
                </div>

                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Repeat On</p>
                  <div className="flex flex-wrap gap-2">
                    {WEEKDAY_OPTIONS.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => toggleRecurringWeekday(option.key)}
                        aria-pressed={recurringWeekdays.includes(option.key)}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                          recurringWeekdays.includes(option.key)
                            ? 'bg-[#304A8A] text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {option.short}
                      </button>
                    ))}
                  </div>
                </div>

                {recurringStartDate && recurringEndDate && parseLocalDate(recurringEndDate) < parseLocalDate(recurringStartDate) ? (
                  <InlineFeedback
                    type="warning"
                    message="Recurring end date must be on or after the start date."
                  />
                ) : null}

                <p className="text-xs text-gray-600">
                  Preview: {recurringGeneratedDates.length} recurring date{recurringGeneratedDates.length === 1 ? '' : 's'} generated.
                </p>
              </div>
            ) : null}
          </div>

          {selectedDates.length === 0 && (!isRecurringEnabled || recurringGeneratedDates.length === 0) ? (
            <InlineFeedback
              type="info"
              className="mt-4"
              message="Choose at least one date in the calendar or configure recurring dates before selecting time slots."
            />
          ) : (
            <>
              <div className="mt-4 flex flex-wrap gap-2">
                {timeSlots.map((time) => (
                  <button
                    key={time}
                    type="button"
                    onClick={() => handleTimeToggle(time)}
                    aria-pressed={selectedTimes.includes(time)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                      selectedTimes.includes(time)
                        ? 'bg-[#6D3B7E] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {time}
                  </button>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={isAddSubmitting || totalDatesToApply === 0 || selectedTimes.length === 0}
                  onClick={handleAddAvailability}
                  className="rounded-full bg-[#10B981] px-5 py-2 text-sm font-semibold text-white hover:bg-[#0e9f6e] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isAddSubmitting ? 'Saving availability...' : 'Add Availability'}
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedTimes([])}
                  disabled={!selectedTimes.length || isAddSubmitting}
                  className="rounded-full bg-gray-100 px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Clear Time Selection
                </button>
              </div>
            </>
          )}
        </section>

        <section className="rounded-2xl bg-white p-5 shadow-sm" aria-label="Upcoming schedule and deletion flow">
          <h2 className="text-lg font-semibold text-[#4B2A56]">Current Upcoming Availability</h2>
          <p className="mt-1 text-sm text-gray-600">
            Select one or more slots below, then hold the delete button to remove them.
          </p>

          {upcomingSchedule.length === 0 ? (
            <section className="mt-4 rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center">
              <h3 className="text-lg font-semibold text-[#6D3B7E]">No future availability</h3>
              <p className="mt-1 text-sm text-gray-600">
                Add dates and time slots above to publish your next openings.
              </p>
            </section>
          ) : (
            <div className="mt-4 space-y-4">
              {upcomingSchedule.map((entry) => (
                <article key={entry.id} className="rounded-xl border border-gray-200 bg-[#FCFBFE] p-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">{entry.date}</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {entry.future_times.map((time) => {
                      const id = `${entry.date}|${time}`;
                      const selected = selectedForDeletion.includes(id);

                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => toggleSelectTime(entry.date, time)}
                          aria-pressed={selected}
                          className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition ${
                            selected
                              ? 'border-red-600 bg-red-500 text-white'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {time}
                        </button>
                      );
                    })}
                  </div>
                </article>
              ))}
            </div>
          )}

          {selectedForDeletion.length > 0 ? (
            <div className="mt-5 space-y-2">
              <p className="text-xs text-gray-600">
                Hold to confirm deletion of {selectedForDeletion.length} selected slot{selectedForDeletion.length === 1 ? '' : 's'}.
              </p>
              <button
                type="button"
                disabled={isDeleteSubmitting}
                onMouseDown={startDeleteHold}
                onMouseUp={stopDeleteHold}
                onMouseLeave={stopDeleteHold}
                onTouchStart={startDeleteHold}
                onTouchEnd={stopDeleteHold}
                onKeyDown={(event) => {
                  if (event.key === ' ' || event.key === 'Enter') {
                    event.preventDefault();
                    startDeleteHold();
                  }
                }}
                onKeyUp={(event) => {
                  if (event.key === ' ' || event.key === 'Enter') {
                    event.preventDefault();
                    stopDeleteHold();
                  }
                }}
                className="relative overflow-hidden rounded-full bg-red-600 px-6 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span
                  className="absolute inset-y-0 left-0 bg-red-800/50 transition-all duration-75"
                  style={{ width: `${deleteHoldProgress}%` }}
                  aria-hidden="true"
                />
                <span className="relative">
                  {isDeleteSubmitting
                    ? 'Deleting...'
                    : isDeleteHoldActive
                      ? 'Keep holding...'
                      : `Hold to Confirm Deletion (${selectedForDeletion.length})`}
                </span>
              </button>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
};

export default WorkerAvailability;
